import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Typography, Spin, Empty, Button, App } from 'antd';
import { DashboardOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// Import Modular Components
import DashboardPerformanceSummaryCards from './components/DashboardPerformanceSummaryCards';
import DashboardSummaryCards from './components/DashboardSummaryCards';
import LeadsByAreaCard from './components/LeadsByAreaCard';
import DashboardCharts from './components/DashboardCharts';
import LeadsBySourceCard from './components/LeadsBySourceCard';
import LeadsProvinceMapCard from './components/LeadsProvinceMapCard';
import RecentLeadsTableCard from './components/RecentLeadsTableCard';
// TARGET REALISASI TABLE: Sementara dinonaktifkan atas permintaan tim. 
// Jika dibutuhkan kembali, silakan aktifkan baris di bawah ini dan komponen di bagian render.
// import TargetRealisasiTable from './TargetRealisasiTable';
// import TargetRealisasiAgentTable from './TargetRealisasiAgentTable';

const { Title, Text } = Typography;

const Dashboard = () => {
  const { user, hasPermission } = useAuth();
  const [statsData, setStatsData] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [leadStatuses, setLeadStatuses] = useState<any[]>([]);
  const [leadSources, setLeadSources] = useState<any[]>([]);
  const [leadFollowUpStatuses, setLeadFollowUpStatuses] = useState<any[]>([]);

  const [leads, setLeads] = useState<any[]>([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });

  const [propertyAreas, setPropertyAreas] = useState<any[]>([]);
  const [selectedAreaIds, setSelectedAreaIds] = useState<string[]>([]);
  const [areaFilterLoading, setAreaFilterLoading] = useState(true);

  const [mapError, setMapError] = useState(false);
  const navigate = useNavigate();
  const { message } = App.useApp();

  // Fetch Stats Data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setStatsLoading(true);
        const [statsRes, statusesRes, sourcesRes, followUpStatusesRes] = await Promise.all([
          api.get('dashboard'),
          api.get('lead-status'),
          api.get('lead-sources'),
          api.get('lead-follow-up-status')
        ]);
        setStatsData(statsRes.data.data);
        setLeadStatuses(statusesRes.data.data);
        setLeadSources(sourcesRes.data.data);
        setLeadFollowUpStatuses(followUpStatusesRes.data.data);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
        message.error('Gagal memuat data dashboard.');
      } finally {
        setStatsLoading(false);
      }
    };
    fetchDashboardData();
  }, [message]);

  const { current: currentPage, pageSize } = pagination;

  // Fetch Recent Leads
  useEffect(() => {
    const fetchRecentLeads = async () => {
      try {
        setTableLoading(true);
        const response = await api.get('dashboard/recent-leads', {
          params: {
            page: currentPage,
            limit: pageSize,
          },
        });
        setLeads(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.meta.total,
        }));
      } catch (error) {
        console.error("Failed to fetch recent leads:", error);
        message.error('Gagal memuat leads terbaru.');
      } finally {
        setTableLoading(false);
      }
    };
    fetchRecentLeads();
  }, [currentPage, pageSize, message]);

  // Fetch Property Areas Filter Options
  useEffect(() => {
    const fetchPropertyAreas = async () => {
      try {
        setAreaFilterLoading(true);
        const response = await api.get('property-areas/role');
        setPropertyAreas(response.data.data);
      } catch (error) {
        console.error("Failed to fetch property areas:", error);
        message.error('Gagal memuat data area properti.');
      } finally {
        setAreaFilterLoading(false);
      }
    };
    fetchPropertyAreas();
  }, [message]);

  // Filter Leads by Selected Property Areas
  const filteredLeadsByArea = useMemo(() => {
    if (!statsData?.leads_by_area) {
      return [];
    }
    if (selectedAreaIds.length === 0) {
      return statsData.leads_by_area;
    }
    return statsData.leads_by_area.filter((area: any) =>
      selectedAreaIds.includes(area.property_area_id)
    );
  }, [statsData, selectedAreaIds]);

  const handleTableChange = (newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
  };

  if (statsLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        background: 'radial-gradient(circle at 50% 0%, #f8fafc 0%, #e2e8f0 100%)'
      }}>
        <Spin size="large" />
        <Text style={{ marginTop: 16, fontWeight: 600, color: '#475569' }}>Membuat dashboard leads...</Text>
      </div>
    );
  }

  if (!statsData) {
    return (
      <div style={{ padding: '80px', textAlign: 'center', background: 'radial-gradient(circle at 50% 0%, #f8fafc 0%, #e2e8f0 100%)', minHeight: '100vh' }}>
        <Empty description="Gagal memuat data dashboard" image={Empty.PRESENTED_IMAGE_SIMPLE}>
          <Button
            type="primary"
            onClick={() => window.location.reload()}
            style={{ backgroundColor: '#7BAA18', borderColor: '#7BAA18', borderRadius: '6px' }}
          >
            Coba Lagi
          </Button>
        </Empty>
      </div>
    );
  }

  return (
    <div style={{
      padding: '24px',
      minHeight: '100vh',
      background: 'radial-gradient(circle at 50% 0%, #f8fafc 0%, #e2e8f0 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Dynamic Style Injection for Premium Glassmorphic Visuals & Interactive Hover Effects */}
      <style>{`
        .glass-card-interactive {
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1) !important;
        }
        .glass-card-interactive:hover {
          transform: translateY(-5px);
          box-shadow: 0 16px 40px rgba(123, 170, 24, 0.08), 0 4px 12px rgba(0, 0, 0, 0.02) !important;
          border-color: rgba(123, 170, 24, 0.35) !important;
        }

        .glass-table-container {
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1) !important;
        }
        .glass-table-container:hover {
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.04) !important;
          border-color: rgba(255, 255, 255, 0.45) !important;
        }

        .glass-table .ant-table-tbody > tr:hover > td {
          background: rgba(123, 170, 24, 0.04) !important;
          transition: background 0.2s ease;
        }

        .glass-table .ant-table-thead > tr > th {
          background: rgba(248, 250, 252, 0.65) !important;
          font-weight: 700;
          color: #334155;
        }

        /* Make active sorter carets highly visible */
        .glass-table .ant-table-column-sorter-up.active .anticon,
        .glass-table .ant-table-column-sorter-down.active .anticon {
          color: #7BAA18 !important;
          font-size: 13px !important;
          transform: scale(1.25);
          transition: all 0.2s ease;
        }

        .glass-select .ant-select-selector {
          background: rgba(255, 255, 255, 0.45) !important;
          backdrop-filter: blur(8px) !important;
          border-color: rgba(255, 255, 255, 0.3) !important;
          border-radius: 6px !important;
        }
        .glass-select .ant-select-selector:hover {
          border-color: rgba(123, 170, 24, 0.5) !important;
        }
      `}</style>

      {/* Decorative Floating Ambient Orbs (Hardware Accelerated Blur) */}
      <div style={{
        position: 'absolute',
        top: '-150px',
        left: '-150px',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(123, 170, 24, 0.12) 0%, rgba(255, 255, 255, 0) 75%)',
        filter: 'blur(60px)',
        zIndex: 0,
        pointerEvents: 'none',
        willChange: 'transform',
      }} />
      <div style={{
        position: 'absolute',
        top: '30%',
        right: '-200px',
        width: '600px',
        height: '600px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(24, 144, 255, 0.08) 0%, rgba(255, 255, 255, 0) 75%)',
        filter: 'blur(80px)',
        zIndex: 0,
        pointerEvents: 'none',
        willChange: 'transform',
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-150px',
        left: '10%',
        width: '450px',
        height: '450px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(250, 173, 20, 0.06) 0%, rgba(255, 255, 255, 0) 75%)',
        filter: 'blur(70px)',
        zIndex: 0,
        pointerEvents: 'none',
        willChange: 'transform',
      }} />

      {/* Main Content Area */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Title Header */}
        <div style={{ marginBottom: '32px' }}>
          <Title level={2} style={{ margin: 0, color: '#1e293b', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <DashboardOutlined style={{ color: '#7BAA18' }} /> Dashboard Leads Management
          </Title>
          <Text type="secondary" style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
            Pantau arus leads masuk, sebaran geografis daerah, efektivitas media iklan, dan data target realisasi sales secara real-time.
          </Text>
        </div>

        {/* Section 0.5: Performance KPI Scorecards (Role-Based glassmorphic cards, hidden for Admin) */}
        <DashboardPerformanceSummaryCards user={user} hasPermission={hasPermission} />

        {/* Section 1: Statistics Cards */}
        <DashboardSummaryCards
          statsData={statsData}
          user={user}
          loading={statsLoading}
          navigate={navigate}
          leadStatuses={leadStatuses}
        />

        {/* Section 2: Leads by Area Chart */}
        <Row gutter={[20, 20]} style={{ marginBottom: '24px' }}>
          <Col xs={24}>
            <LeadsByAreaCard
              data={filteredLeadsByArea}
              propertyAreas={propertyAreas}
              selectedAreaIds={selectedAreaIds}
              setSelectedAreaIds={setSelectedAreaIds}
              areaFilterLoading={areaFilterLoading}
              leadFollowUpStatuses={leadFollowUpStatuses}
              navigate={navigate}
              loading={statsLoading}
            />
          </Col>
        </Row>

        {/* Section 3: Trend & Stage Distribution Charts */}
        <DashboardCharts
          statsData={statsData}
          leadStatuses={leadStatuses}
          navigate={navigate}
          message={message}
          loading={statsLoading}
        />

        {/* Section 4: Source Leads Column Bar Chart */}
        <LeadsBySourceCard
          statsData={statsData}
          leadSources={leadSources}
          navigate={navigate}
          message={message}
          loading={statsLoading}
        />

        {/* Section 5: Geographical Distribution National Map & Province Grid */}
        <LeadsProvinceMapCard
          statsData={statsData}
          navigate={navigate}
          message={message}
          mapError={mapError}
          setMapError={setMapError}
          loading={statsLoading}
        />

        {/* Section 6: Target vs Realisation Table (Conditional Based on Role Permissions) */}
        {/* SEMENTARA DINONAKTIFKAN (HIDDEN) UNTUK SEMUA ROLE atas permintaan tim. Data ini sudah diakses di halaman Profile/Performance */}
        {/* Untuk mengaktifkan kembali, cukup buka tanda komentar block di bawah ini dan import di atas. */}
        {/* 
        <Row gutter={[20, 20]} style={{ marginBottom: '24px' }}>
          <Col xs={24}>
            {hasPermission('lead.assign') ? (
              <div style={glassTableContainerStyle} className="glass-table-container">
                <div style={{ marginBottom: '16px', padding: '0 8px' }}>
                  <Title level={4} style={{ margin: 0, color: '#1e293b', fontWeight: 700 }}>
                    Pencapaian Target &amp; Realisasi Agen Sales
                  </Title>
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    Rekap perbandingan target vs realisasi leads untuk masing-masing agen sales.
                  </Text>
                </div>
                <TargetRealisasiAgentTable user={user} />
              </div>
            ) : hasPermission('lead.property') ? (
              <div style={glassTableContainerStyle} className="glass-table-container">
                <div style={{ marginBottom: '16px', padding: '0 8px' }}>
                  <Title level={4} style={{ margin: 0, color: '#1e293b', fontWeight: 700 }}>
                    Pencapaian Target &amp; Realisasi Kawasan
                  </Title>
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    Rekap perbandingan target vs realisasi leads untuk masing-masing kawasan properti.
                  </Text>
                </div>
                <TargetRealisasiTable />
              </div>
            ) : null}
          </Col>
        </Row>
        */}

        {/* Section 7: Recent Leads Table */}
        <Row>
          <Col xs={24}>
            <RecentLeadsTableCard
              leads={leads}
              tableLoading={tableLoading}
              pagination={pagination}
              handleTableChange={handleTableChange}
              navigate={navigate}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Dashboard;
